// src/utils/referenceGenerator.ts
export function generateAssetReference(existingReferences: string[]): string {
  const year = new Date().getFullYear();
  const existing = existingReferences
    .filter(ref => ref?.startsWith(`A-${year}-`))
    .map(ref => parseInt(ref.split('-')[2]))
    .filter(n => !isNaN(n));
  const next = existing.length > 0 ? Math.max(...existing) + 1 : 1;
  return `A-${year}-${String(next).padStart(4, '0')}`;
}

export function generateRiskReference(existingReferences: string[]): string {
  const year = new Date().getFullYear();
  const existing = existingReferences
    .filter(ref => ref?.startsWith(`R-${year}-`))
    .map(ref => parseInt(ref.split('-')[2]))
    .filter(n => !isNaN(n));
  const next = existing.length > 0 ? Math.max(...existing) + 1 : 1;
  return `R-${year}-${String(next).padStart(4, '0')}`;
}

// src/utils/dateUtils.ts
import { format, parseISO, isValid, differenceInDays, addMonths } from 'date-fns';

export function formatDate(date: string | Date | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? parseISO(date) : date;
  return isValid(d) ? format(d, 'dd/MM/yyyy') : '-';
}

export function formatDateTime(date: string | Date | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? parseISO(date) : date;
  return isValid(d) ? format(d, 'dd/MM/yyyy HH:mm') : '-';
}

export function calculateExpiry(date: string, periodicityMonths: string): Date | null {
  if (!date || !periodicityMonths) return null;
  const baseDate = parseISO(date);
  const months = parseInt(periodicityMonths);
  if (!isValid(baseDate) || isNaN(months)) return null;
  return addMonths(baseDate, months);
}

export function getDaysUntil(date: Date): number {
  return differenceInDays(date, new Date());
}

export function getRAGStatus(date: Date): 'red' | 'amber' | 'green' {
  const daysUntil = getDaysUntil(date);
  if (daysUntil < 0) return 'red';
  if (daysUntil <= 30) return 'amber';
  return 'green';
}

// src/utils/exportUtils.ts
import type { Asset } from '../types/asset';
import type { Risk } from '../types/risk';

export function exportToCSV(data: Record<string, unknown>[], filename: string): void {
  if (data.length === 0) return;

  const headers = Object.keys(data[0]);
  const rows = data.map(row =>
    headers.map(header => {
      const value = row[header];
      const stringValue = value === null || value === undefined ? '' : String(value);
      return `"${stringValue.replace(/"/g, '""')}"`;
    }).join(',')
  );

  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `${filename}-${format(new Date(), 'yyyy-MM-dd')}.csv`);
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function prepareAssetExportData(assets: Asset[]): Record<string, unknown>[] {
  return assets.map(asset => ({
    'Reference Number': asset.referenceNumber,
    'Make': asset.make || '',
    'Model': asset.model || '',
    'Asset Group': asset.assetGroup || '',
    'SKU': asset.sku,
    'Quantity': asset.quantity,
    'Location': asset.location,
    'Asset Owner': asset.assetOwner,
    'ISO 27001': asset.isISO27001 ? 'Yes' : 'No',
    'ISO 27001 Category': asset.iso27001Category || '',
    'Audit Date': asset.auditDate,
    'Audit Periodicity': asset.auditPeriodicity,
    'Calibration Date': asset.calibrationDate,
    'Calibration Periodicity': asset.calibrationPeriodicity,
    'Certification Date': asset.certificationDate,
    'Certification Periodicity': asset.certificationPeriodicity,
    'Test Date': asset.testDate,
    'Test Periodicity': asset.testPeriodicity,
    'Notes': asset.notes,
  }));
}

export function prepareRiskExportData(risks: Risk[]): Record<string, unknown>[] {
  return risks.map(risk => ({
    'Reference Number': risk.referenceNumber,
    'Asset Category': risk.assetCategory,
    'Asset': risk.asset,
    'Asset Group': risk.assetGroup || '',
    'Asset Group Count': risk.assetGroupCount || 0,
    'Asset Link Type': risk.assetLinkType || 'single',
    'Threat': risk.threat,
    'Vulnerability': risk.vulnerability,
    'Risk Owner': risk.riskOwner,
    'Risk Category': risk.riskCategory.join(', '),
    'Uncontrolled Likelihood': risk.ucLikelihood,
    'Uncontrolled Impact': risk.ucImpact,
    'Treatment': risk.treatment,
    'Treatment Detail': risk.treatmentDetail,
    'Managed Likelihood': risk.managedLikelihood,
    'Managed Impact': risk.managedImpact,
    'Controls': (risk.controls?.length > 0 ? risk.controls : risk.control ? [{ control: risk.control, controlImplementation: risk.controlImplementation }] : []).map(c => c.control).join('; '),
    'Control Implementations': (risk.controls?.length > 0 ? risk.controls : risk.control ? [{ control: risk.control, controlImplementation: risk.controlImplementation }] : []).map(c => c.controlImplementation).filter(Boolean).join('; '),
    'Control Status': risk.controlStatus,
    'Date Completed': risk.dateCompleted,
    'Review Period': risk.maxReviewPeriod,
    'Last Reviewed': risk.lastDateReviewed,
    'Notes': risk.notes,
  }));
}

// src/utils/riskMatrix.ts
import type { Likelihood, Impact } from '../types/risk';

export const riskMatrix: Record<Likelihood, Record<Impact, { score: number; color: string }>> = {
  'Almost Certain': {
    'Insignificant': { score: 5, color: 'bg-yellow-400' },
    'Minor': { score: 10, color: 'bg-orange-400' },
    'Moderate': { score: 15, color: 'bg-red-500' },
    'Serious': { score: 20, color: 'bg-red-600' },
    'Catastrophic': { score: 25, color: 'bg-red-700' },
  },
  'Likely': {
    'Insignificant': { score: 4, color: 'bg-green-400' },
    'Minor': { score: 8, color: 'bg-yellow-400' },
    'Moderate': { score: 12, color: 'bg-orange-400' },
    'Serious': { score: 16, color: 'bg-red-500' },
    'Catastrophic': { score: 20, color: 'bg-red-600' },
  },
  'Possible': {
    'Insignificant': { score: 3, color: 'bg-green-400' },
    'Minor': { score: 6, color: 'bg-yellow-400' },
    'Moderate': { score: 9, color: 'bg-yellow-400' },
    'Serious': { score: 12, color: 'bg-orange-400' },
    'Catastrophic': { score: 15, color: 'bg-red-500' },
  },
  'Unlikely': {
    'Insignificant': { score: 2, color: 'bg-green-400' },
    'Minor': { score: 4, color: 'bg-green-400' },
    'Moderate': { score: 6, color: 'bg-yellow-400' },
    'Serious': { score: 8, color: 'bg-yellow-400' },
    'Catastrophic': { score: 10, color: 'bg-orange-400' },
  },
  'Rare': {
    'Insignificant': { score: 1, color: 'bg-green-400' },
    'Minor': { score: 2, color: 'bg-green-400' },
    'Moderate': { score: 3, color: 'bg-green-400' },
    'Serious': { score: 4, color: 'bg-green-400' },
    'Catastrophic': { score: 5, color: 'bg-yellow-400' },
  },
};
